(() => {
  if (customElements.get('taylors-mega-menu')) return;

  const FOCUSABLE =
    'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input, select, textarea';
  const MOBILE_BREAKPOINT = 899;

  class TaylorsMegaMenu extends HTMLElement {
    connectedCallback() {
      this.backdrop = this.querySelector('[data-mm-backdrop]');
      this.panel = this.querySelector('[data-mm-panel]');
      this.closeBtn = this.querySelector('[data-mm-close]');
      this.backBtn = this.querySelector('[data-mm-back]');
      this.activeLabel = this.querySelector('[data-mm-active-label]');
      this.navItems = Array.from(this.querySelectorAll('[data-mm-nav]'));
      this.centerPanels = Array.from(this.querySelectorAll('[data-mm-center]'));
      this.promoPanels = Array.from(this.querySelectorAll('[data-mm-promo]'));

      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleOpen = this.open.bind(this);

      this.dataset.mobileView = 'root';

      document.addEventListener('mega-menu:open', this.handleOpen);
      this.backdrop?.addEventListener('click', () => this.close());
      this.closeBtn?.addEventListener('click', () => this.close());
      this.backBtn?.addEventListener('click', () => this.drillBack());

      this.navItems.forEach((item) => {
        item.addEventListener('click', () => {
          const slug = item.dataset.mmNav;
          this.activate(slug);
          if (this.isMobile()) this.drillTo(slug);
        });
      });

      const initial = this.navItems.find((n) => n.classList.contains('is-active'));
      if (initial) this.activate(initial.dataset.mmNav, { animate: false });
    }

    disconnectedCallback() {
      document.removeEventListener('mega-menu:open', this.handleOpen);
      document.removeEventListener('keydown', this.handleKeydown);
      document.body.classList.remove('mm-open');
    }

    isMobile() {
      return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches;
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      const trigger = document.querySelector('[data-mega-menu-trigger]');
      if (trigger instanceof HTMLElement) trigger.setAttribute('aria-expanded', 'true');
      this.dataset.mobileView = 'root';
      document.body.classList.add('mm-open');
      document.addEventListener('keydown', this.handleKeydown);
      requestAnimationFrame(() => {
        const first = this.querySelector(FOCUSABLE);
        first?.focus();
      });
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.dataset.mobileView = 'root';
      document.body.classList.remove('mm-open');
      document.removeEventListener('keydown', this.handleKeydown);
      const trigger = document.querySelector('[data-mega-menu-trigger]');
      if (trigger instanceof HTMLElement) {
        trigger.setAttribute('aria-expanded', 'false');
        trigger.focus();
      }
    }

    drillTo(slug) {
      if (!slug) return;
      this.dataset.mobileView = slug;
      const navItem = this.navItems.find((n) => n.dataset.mmNav === slug);
      const label = navItem?.querySelector('.mm__nav-label')?.textContent?.trim() || '';
      if (this.activeLabel) this.activeLabel.textContent = label;
    }

    drillBack() {
      this.dataset.mobileView = 'root';
    }

    activate(slug, { animate = true } = {}) {
      if (!slug) return;
      this.navItems.forEach((n) => {
        const active = n.dataset.mmNav === slug;
        n.classList.toggle('is-active', active);
        n.setAttribute('aria-expanded', String(active));
      });
      this.centerPanels.forEach((p) => {
        const match = p.dataset.mmCenter === slug;
        p.classList.toggle('is-active', match);
        p.setAttribute('aria-hidden', String(!match));
        if (match && animate) {
          p.classList.remove('is-animating');
          requestAnimationFrame(() => p.classList.add('is-animating'));
        }
      });
      this.promoPanels.forEach((p) => {
        const match = p.dataset.mmPromo === slug;
        p.classList.toggle('is-active', match);
        p.setAttribute('aria-hidden', String(!match));
      });
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (this.isMobile() && this.dataset.mobileView !== 'root') {
          this.drillBack();
        } else {
          this.close();
        }
        return;
      }
      if (event.key === 'Tab') {
        const focusables = Array.from(this.querySelectorAll(FOCUSABLE)).filter(
          (el) => el.offsetParent !== null,
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          if (last instanceof HTMLElement) last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          if (first instanceof HTMLElement) first.focus();
        }
      }
    }
  }

  customElements.define('taylors-mega-menu', TaylorsMegaMenu);
})();
